import { type ChangeEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputRadio } from '@/components/InputRadio/InputRadio'
import { InputSelect } from '@/components/InputSelect/InputSelect'
import { usePriceIntentValueOrThrow } from '@/features/priceCalculator/usePriceIntent'
import { useExternalInsurersQuery, usePriceIntentInsurelyUpdateMutation } from '@/services/graphql/generated'
import { useTracking } from '@/services/Tracking/useTracking'
import { datadogRum } from '@datadog/browser-rum'
import { yStack } from 'ui/src/patterns/stack'
import { FetchedInsuranceCard } from './FetchedInsuranceCard'
import { useExternalInsurerId } from './useExternalInsurerId'

const CURRENT_INSURANCE_FIELD_NAME = 'alreadyHaveInsurance'

enum RadioOption {
  YES = 'YES',
  NO = 'NO',
  UNANSWERED = '',
}

type Props = {
  label: string
  name: string
}

export function CurrentInsuranceField({ label, name }: Props) {
  const { t } = useTranslation('purchase-form')
  const tracking = useTracking()

  const priceIntent = usePriceIntentValueOrThrow()
  const [updateDataCollectionId, { loading: updateDataCollectionIdLoading }] = usePriceIntentInsurelyUpdateMutation()
  const companyOptions = useCompanyOptions()
  const [externalInsurerId, setExternalInsurerId, changeExternalInsurerLoading] = useExternalInsurerId()
  const [switching, setSwitching] = useState<RadioOption>(() => {
    if (externalInsurerId) {
      return RadioOption.YES
    } else {
      const hasPriceIntentBeenSubmitted = priceIntent.offers.length > 0
      // If price intent has been submitted and no external insurer has been selected,
      // it means users have stated before that they don't have insurance.
      if (hasPriceIntentBeenSubmitted) {
        return RadioOption.NO
      } else {
        return RadioOption.UNANSWERED
      }
    }
  })

  const handleRadioValueChange = (value: RadioOption) => {
    datadogRum.addAction('Enable switching', { value })
    setSwitching(value)

    if (value === RadioOption.NO) {
      // Avoids an unnecessary network request
      if (externalInsurerId) {
        // Reset data collection id + external insurer id
        updateDataCollectionId({
          variables: { priceIntentId: priceIntent.id },
          onCompleted() {
            setExternalInsurerId()
          },
        })
      }
    } else {
      tracking.reportInsurelyPrompted({
        id: priceIntent.product.id,
        displayNameFull: priceIntent.product.displayNameFull,
      })
    }
  }

  const handleChangeExternalInsurer: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const newExternalInsurer = event.target.value
    datadogRum.addAction('Select ExternalInsurer', { value: newExternalInsurer })
    setExternalInsurerId(newExternalInsurer)
  }

  const isLoading = updateDataCollectionIdLoading || changeExternalInsurerLoading
  const hasFetchedInsurances = (priceIntent.fetchedExternalInsurances?.length ?? 0) > 0

  return (
    <>
      <div className={yStack({ gap: 'xxs' })}>
        <InputRadio
          // BE doesn't care about the value of this field. We use it to decide among UI changes.
          // However, we need to treat it as required to make sure users answer it and mark the form
          // as invalid in case they don't. With that said, a 'name' attribute is required for native
          // form validation to work.
          name={CURRENT_INSURANCE_FIELD_NAME}
          label={label}
          value={switching}
          onValueChange={handleRadioValueChange}
          options={[
            { label: t('LABEL_YES'), value: RadioOption.YES },
            { label: t('LABEL_NO'), value: RadioOption.NO },
          ]}
          disabled={isLoading}
          required
        />

        {switching === RadioOption.YES && (
          <>
            {priceIntent.hasCollectedInsurelyData ? (
              <FetchedInsuranceCard
                insuranceCompany={priceIntent.externalInsurer!.displayName}
                hasFetchedInsurances={hasFetchedInsurances}
              />
            ) : (
              <>
                <InputSelect
                  name={name}
                  required
                  options={companyOptions}
                  placeholder={t('CURRENT_INSURANCE_FIELD_PLACEHOLDER')}
                  value={externalInsurerId}
                  onChange={handleChangeExternalInsurer}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}

function useCompanyOptions() {
  const priceIntent = usePriceIntentValueOrThrow()
  const { data } = useExternalInsurersQuery({ variables: { productName: priceIntent.product.name } })
  const companyOptions = data?.product?.externalInsurers.map((item) => ({
    name: item.displayName,
    value: item.id,
  }))
  return companyOptions ?? []
}
